import TimerList from '@/constants/TimerList';
import { convertCentsToDollars, convertDollarToCents } from '@thdinc/hpf-shared';
import { format } from 'date-fns';
import { formatInTimeZone, toZonedTime } from 'date-fns-tz';

export const timeToAmPm = (time: string, timezone: string) => {
	return TimerList.find(
		(t) => t.value === format(toZonedTime(time, timezone), 'HH:mm').toString(),
	)?.label;
};

export const parseTime = (timeStr: string) => {
	if (!timeStr) return NaN;
	const [time, modRaw] = timeStr.split(' ');
	const [rawHours, minutes] = time.split(':').map(Number);
	const modifier = (modRaw || '').toUpperCase();
	let hours = rawHours;
	if (modifier === 'PM' && hours !== 12) hours += 12;
	if (modifier === 'AM' && hours === 12) hours = 0;
	return hours * 60 + minutes; // minutes since midnight
};
export const diffMinutesSameDay = (startStr: string, endStr: string) =>
	parseTime(endStr) - parseTime(startStr);

/** Minutes between start and end, treating end as next day when end <= start (e.g. 2 AM after 11 PM). */
export const diffMinutesAllowNextDay = (startStr: string, endStr: string): number => {
	const startMin = parseTime(startStr);
	let endMin = parseTime(endStr);
	if (endMin <= startMin) endMin += 24 * 60;
	return endMin - startMin;
};

const MIN_EVENT_GAP_MINUTES = 30;
/** Next-day end times: only 12:00 AM–2:00 AM (minutes 0–120). Excludes 7 AM–11:30 PM "next day". */
const NEXT_DAY_END_MAX_MINUTES = 2 * 60; // 2:00 AM

/** Get the option that is exactly `minutes` after start (e.g. 30). Returns option value or undefined. */
export const getEndTimeMinutesAfter = (
	startTime: string,
	minutes: number,
	allOptions: { value: string; name?: string }[],
): string | undefined => {
	if (!startTime) return undefined;
	const startMin = parseTime(startTime);
	for (const opt of allOptions) {
		let endMin = parseTime(opt.value);
		if (endMin <= startMin) endMin += 24 * 60;
		if (endMin - startMin === minutes) return opt.value;
	}
	return undefined;
};

/** Filter end-time options: same day start+30min or next day 12 AM–2 AM only (no 7 AM–start time as "next day"). */
export const getValidEndOptions = (
	startTime: string,
	allOptions: { value: string; name?: string }[],
) => {
	if (!startTime) return allOptions;
	const startMin = parseTime(startTime);
	return allOptions.filter((opt) => {
		const endMin = parseTime(opt.value);
		// Same day: must be after start and at least 30 min later
		if (endMin > startMin) return endMin - startMin >= MIN_EVENT_GAP_MINUTES;
		// Next day: only include 12:00 AM through 2:00 AM (so dropdown doesn't show 7 AM–start)
		if (endMin <= startMin) return endMin <= NEXT_DAY_END_MAX_MINUTES;
		return false;
	});
};

export const toCaptilize = (value: string) => {
	return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
};

export const twoDaysBeforeFormatted = (date?: Date | string | null): string => {
	if (!date) return '';

	const d = new Date(date);
	d.setDate(d.getDate() - 2);

	const month = String(d.getMonth() + 1).padStart(2, '0'); // months start from 0
	const day = String(d.getDate()).padStart(2, '0');
	const year = d.getFullYear();

	return `${month}/${day}/${year}`;
};
export const twoDaysBeforeEventDate = (date?: Date | string | null): Date | null => {
	if (!date) return null;
	const d = new Date(date);
	d.setDate(d.getDate() - 3);
	return d;
};
export const formattedDate = (date?: Date | string | null): string => {
	if (!date) return '';

	const d = new Date(date);
	d.setDate(d.getDate());

	const month = String(d.getMonth() + 1).padStart(2, '0'); // months start from 0
	const day = String(d.getDate()).padStart(2, '0');
	const year = d.getFullYear();

	return `${month}/${day}/${year}`;
};

export function isMoreThan7DaysAway(inputDate: null | Date): boolean {
	if (!inputDate) return false;
	const now = new Date();
	const currentDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
	const target = new Date(inputDate);
	const targetDate = new Date(target.getFullYear(), target.getMonth(), target.getDate());

	const diffInMs = targetDate.getTime() - currentDate.getTime();
	const diffInDays = diffInMs / (1000 * 60 * 60 * 24);

	return diffInDays > 7;
}

/** If endTime is "before" startTime in clock terms (e.g. 2 AM vs 11 PM), end is next day. */
export function isEndTimeNextDay(startTime: string, endTime: string): boolean {
	if (!startTime || !endTime) return false;
	return parseTime(endTime) <= parseTime(startTime);
}

export function combineEventDateAndTime(
	eventDate: Date | null,
	startTime: string,
	nextDay = false,
): string {
	if (!eventDate || !startTime) return '';

	// Parse the time string "hh:mm AM/PM"
	const [timePart, period] = startTime.split(' ');
	const [hourStr, minuteStr] = timePart.split(':');
	let hours = parseInt(hourStr, 10);
	const minutes = parseInt(minuteStr, 10);

	// Convert 12-hour format → 24-hour format
	if (period === 'PM' && hours !== 12) hours += 12;
	if (period === 'AM' && hours === 12) hours = 0;

	const combined = new Date(eventDate);
	if (nextDay) combined.setDate(combined.getDate() + 1);
	combined.setHours(hours);
	combined.setMinutes(minutes);
	combined.setSeconds(0);
	combined.setMilliseconds(0);

	return combined.toISOString();
}

/**
 * Combines event date and time into an ISO-like string using the date's calendar date
 * and the given time, without timezone conversion. Use when the API expects the time
 * to be in event-local form (payload also sends timezone separately).
 * Returns "YYYY-MM-DDTHH:mm:00" so 1:00 PM appears as 13:00 in the payload.
 */
export function combineEventDateAndTimeLocal(
	eventDate: Date | null,
	startTime: string,
	nextDay = false,
): string {
	if (!eventDate || !startTime) return '';

	const [timePart, period] = startTime.split(' ');
	const [hourStr, minuteStr] = (timePart || '').split(':');
	let hours = parseInt(hourStr, 10) || 0;
	const minutes = parseInt(minuteStr, 10) || 0;

	if ((period || '').toUpperCase() === 'PM' && hours !== 12) hours += 12;
	if ((period || '').toUpperCase() === 'AM' && hours === 12) hours = 0;

	const y = eventDate.getFullYear();
	const m = eventDate.getMonth();
	const d = eventDate.getDate();
	const base = new Date(y, m, d);
	if (nextDay) base.setDate(base.getDate() + 1);
	const yy = base.getFullYear();
	const mm = String(base.getMonth() + 1).padStart(2, '0');
	const dd = String(base.getDate()).padStart(2, '0');
	const hh = String(hours).padStart(2, '0');
	const min = String(minutes).padStart(2, '0');
	return `${yy}-${mm}-${dd}T${hh}:${min}:00`;
}

export function getTwoDaysBeforeISO(isoDate: Date | null): string {
	if (!isoDate) return '';

	const date = new Date(isoDate);
	if (isNaN(date.getTime())) return '';

	const result = new Date(date);
	result.setUTCDate(result.getUTCDate() - 2);

	return result.toISOString();
}

export function splitEventDateAndTime(isoString: string): {
	eventDate: Date | null;
	formattedTime: string;
    time: string;
} {
	if (!isoString) {
		return { eventDate: null, formattedTime: '', time:'' };
	}

	const date = new Date(isoString);

	if (isNaN(date.getTime())) {
		return { eventDate: null, formattedTime: '', time:'' };
	}

	// Extract hours and minutes (UTC, because toISOString() was used)
	let hours = date.getUTCHours();
	const minutes = date.getUTCMinutes();

	// Convert 24-hour → 12-hour format (match TimeOptions: "07:00 AM", "12:00 PM")
	const period = hours >= 12 ? 'PM' : 'AM';
	hours = hours % 12;
	if (hours === 0) hours = 12;

	const paddedHours = hours.toString().padStart(2, '0');
	const paddedMinutes = minutes.toString().padStart(2, '0');

	return {
		// Keep only the date portion
		eventDate: new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()),
		formattedTime: `${paddedHours}:${paddedMinutes} ${period}`,
        time: `${paddedHours}:${paddedMinutes}`,
	};
}

/**
 * Same as splitEventDateAndTime but converts the ISO string to the given timezone
 * (or browser local time if no timezone) before extracting date and time.
 * Uses formatInTimeZone so the displayed time is correct in that zone (avoids
 * toZonedTime + getHours() returning UTC and flipping PM/AM).
 */
export function splitEventDateAndTimeInZone(
	isoString: string,
	timezone?: string,
): {
	eventDate: Date | null;
	formattedTime: string;
} {
	if (!isoString) {
		return { eventDate: null, formattedTime: '' };
	}

	const utcDate = new Date(isoString);
	if (isNaN(utcDate.getTime())) {
		return { eventDate: null, formattedTime: '' };
	}

	if (timezone) {
		// Use formatInTimeZone so hours/minutes are in the target zone (not UTC)
		const hour24 = parseInt(formatInTimeZone(utcDate, timezone, 'H'), 10);
		const minutes = parseInt(formatInTimeZone(utcDate, timezone, 'm'), 10);
		const year = parseInt(formatInTimeZone(utcDate, timezone, 'yyyy'), 10);
		const month = parseInt(formatInTimeZone(utcDate, timezone, 'M'), 10) - 1;
		const day = parseInt(formatInTimeZone(utcDate, timezone, 'd'), 10);

		const period = hour24 >= 12 ? 'PM' : 'AM';
		let hours12 = hour24 % 12;
		if (hours12 === 0) hours12 = 12;

		return {
			eventDate: new Date(year, month, day),
			formattedTime: `${hours12.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`,
		};
	}

	// No timezone: use local date getters
	const date = utcDate;
	let hours = date.getUTCHours();
	const minutes = date.getMinutes();
	const period = hours >= 12 ? 'PM' : 'AM';
	hours = hours % 12;
	if (hours === 0) hours = 12;

	return {
		eventDate: new Date(date.getFullYear(), date.getMonth(), date.getDate()),
		formattedTime: `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')} ${period}`,
	};
}

export function mapBartenderPackagesForFrontend(data: any[] | null | undefined) {
	if (!Array.isArray(data) || data.length === 0) {
		return [];
	}

	return data.map((d) => ({
		id: d?.id,
		provider: d?.provider,
		name: d?.name,
		qty: Number(d?.qty),
		costPerItem: Number(convertCentsToDollars(String(d?.cost_per_item))),
		profitPerItem: Number(convertCentsToDollars(String(d?.profit_per_item))),
		pricePerItem: Number(convertCentsToDollars(String(d?.price_per_item))),
		lineTotal: Number(convertCentsToDollars(String(d?.line_total))),
	}));
}

export function mapCocktailItemsForFrontend(data: any[] | null | undefined) {
	if (!Array.isArray(data) || data.length === 0) {
		return [];
	}
	return data.map((d) => ({
		id: d?.id,
		provider: d?.provider,
		qty: Number(d?.quantity),
		type: d?.type,
		cocktails: d?.cocktail_name,
		brand: d?.product_id,
		brandName: d?.brand_name,
		pricePerItem: Number(d?.price_per_item),
	}));
}

export const payloadDrinkType = (drinkType: any, value: any) => {
	if (!drinkType) return null;

	if (drinkType === 'cocktails' && value === 'other') return 'others';
	if (drinkType === 'cocktails' && value !== 'other') return 'cocktail';

	if (drinkType === 'products') return 'product';

	return null;
};

export const getCocktailId = (item: any): string | null => {
	if (!item?.cocktails) return null;
	if (item.cocktails.id === 'custom') return null;
	return item.cocktails.id ?? null;
};

export const getProductId = (item: any): string | null => {
	if (!item?.brand) return null;
	if (item.brand.id === 'other') return null;
	return item.brand.id ?? null;
};

export const getBrandName = (item: any): string | null => {
	if (!item?.brand) return item?.brandName ?? null;

	if (item.brand.id === 'other') {
		return item?.brandName ?? null;
	}

	return item.brand.name ?? null;
};

export const toNumberString = (value: unknown): string => {
	if (value === null || value === undefined || value === '') {
		return '0';
	}
	return String(value);
};

export function dollarsToCents(dollars: number): number {
	if (!Number.isFinite(dollars)) {
		throw new Error('Invalid dollar amount');
	}
	return Math.round(dollars * 100);
}

export function centsToDollors(cents: number): number {
	if (!Number.isFinite(cents)) {
		throw new Error('Invalid dollar amount');
	}
	const value = cents / 100;
	return Number(value.toFixed(2));
}

export function getTimeDifference(start: any, end: any) {
	const diffMs = end - start;

	return {
		hours: diffMs / (1000 * 60 * 60),
		minutes: diffMs / (1000 * 60),
	};
}
